import React, { useState, useEffect, useRef } from 'react';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import '../styles/global.css';
import { Dropdown } from 'primereact/dropdown';
import { ProgressBar } from 'primereact/progressbar';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { Fieldset } from 'primereact/fieldset';
import { RadioButton } from 'primereact/radiobutton';
import { Toast } from 'primereact/toast';
import { FaRegTimesCircle } from 'react-icons/fa';
import * as L from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
import * as mapas from '../utils/mapas';
import { API_URL } from '../api/API';
import { ReactComponent as AlgodaoIcon } from '../images/algodao.svg';
import { ReactComponent as SojaIcon } from '../images/soja.svg';
import { ReactComponent as MosaicoIcon } from '../images/mosaico/MOSAICO 2.svg';
import { ReactComponent as DamineIcon } from '../images/mosaico/DAMINE.svg';
import { ReactComponent as GotaIcon } from '../images/mosaico/Mosaico.svg';

enum ProcessMethod {
    RegressaoLinear = 'RL',
    RedeNeural = 'RN'
}

export default function ProcessaCar() {
    const [ufs, setUfs] = useState<any[]>([]);
    const [uf, setUf] = useState<any>(null);
    const [municipios, setMunicipios] = useState<any[]>([]);
    const [municipio, setMunicipio] = useState<any>(null);

    const mapRef = useRef<any>(null);
    const [mapa, setMapa] = useState<L.Map | null>(null);

    const [loading, setLoading] = useState(false);
    const [poligonosCarMap, setPoligonosCarMap] = useState<mapas.AllType>();
    const [cars, setCars] = useState<mapas.CAR[]>([]);
    const [car, setCar] = useState<any>();

    const toastRef = useRef<Toast | null>(null);

    // Variáveis do Modal de Processamento
    const [dialogProcessarVisivel, setDialogProcessarVisivel] = useState(false);
    const [processType, setProcessType] = useState<'soja' | 'algodao'>('soja');
    const [processMethod, setProcessMethod] = useState<ProcessMethod>(ProcessMethod.RegressaoLinear);
    const [dates, setDates] = useState<Date[]>([]);

    const minDate = new Date();
    minDate.setFullYear(minDate.getFullYear() - 5);
    const maxDate = new Date();

    // Variáveis da Barra de Progresso
    const [dialogVisivel, setDialogVisivel] = useState(false);
    const [progresso, setProgresso] = useState(0);
    const [mensagemLoading, setMensagemLoading] = useState('');
    const [apiConcluida, setApiConcluida] = useState(false);
    const [erroProcessar, setErroProcessar] = useState<boolean>(false);
    const [segundos, setSegundos] = useState(0);

    const intervaloProgressoRef = useRef<any>(null);
    const segundosIntervalRef = useRef<any>(null);
    const segundosRef = useRef(0);
    const inicioTimerRef = useRef<any>(null);
    const CINCO_MINUTOS_MS = 2 * 60 * 1000;

    // 1. Carregar os Estados (UFs) via API do IBGE ao montar o componente
    useEffect(() => {
        fetch('https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome')
            .then(res => res.json())
            .then(data => setUfs(data))
            .catch(err => console.error('Erro ao buscar UFs:', err));

        return () => {
            if (intervaloProgressoRef.current) clearInterval(intervaloProgressoRef.current);
            if (segundosIntervalRef.current) clearInterval(segundosIntervalRef.current);
        };
    }, []);

    // 2. Carregar Municípios quando a UF for selecionada
    useEffect(() => {
        if (uf) {
            setLoading(true);
            fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf.id}/municipios?orderBy=nome`)
                .then(res => res.json())
                .then(data => {
                    setMunicipios(data);
                    setLoading(false);
                })
                .catch(err => {
                    console.error('Erro ao buscar municípios:', err);
                    setLoading(false);
                });
            setMunicipio(null);
        } else {
            setMunicipios([]);
        }
    }, [uf]);

    // 3. Inicializar o Mapa
    useEffect(() => {
        try {
            if (!mapRef.current) return;
            if (!mapa) {
                const map = mapas.createMapOnBahia(mapRef);
                mapas.refreshingMap(map, setLoading);
                setMapa(map);
            }
        } catch (e: any) {
            console.log('Error ao criar mapa:', e);
        }
    }, [mapRef.current]);

    // Função padrão de seleção de CARs
    const selectCar = (carLayer: L.GeoJSON, carsList: Array<any>) => {
        carsList.map((p) => { p.setStyle(mapas.estiloArea); });
        carLayer.setStyle(mapas.estiloSelected);
        carLayer.openPopup();
    };

    // 4. Lidar com a seleção do Município
    const onMunicipioChange = async (e: any) => {
        const mun = e.value;
        setMunicipio(mun);

        if (mun && mapa) {
            setLoading(true);
            poligonosCarMap?.municipio?.remove();
            try {
                poligonosCarMap?.cars.forEach((p: any) => p.poligono?.remove() || p.remove());
            } catch (error) { }

            if (typeof global !== 'undefined') {
                (global as any).uf = uf.sigla;
            } else {
                (window as any).global = { ...((window as any).global || {}), uf: uf.sigla };
            }

            const psCarMap = await mapas.loadMunicipioCars(
                mapa, mun, mapas.estiloContorno, true, true, null,
                selectCar, setCar, setCars
            );

            if (psCarMap) {
                setPoligonosCarMap(psCarMap);
                psCarMap.municipio?.openPopup();
                setTimeout(() => {
                    mapa?.invalidateSize();
                    if (psCarMap.municipio) mapa?.fitBounds(psCarMap.municipio.getBounds());
                }, 100);
            }
            setLoading(false);
        }
    };

    // Tracking de segundos para o dialog
    useEffect(() => {
        if (dialogVisivel) {
            segundosIntervalRef.current = setInterval(() => {
                segundosRef.current += 1;
                setSegundos(segundosRef.current);
            }, 1000);
        } else if (segundosIntervalRef.current) {
            clearInterval(segundosIntervalRef.current);
            segundosIntervalRef.current = null;
        }
        return () => {
            if (segundosIntervalRef.current) clearInterval(segundosIntervalRef.current);
        };
    }, [dialogVisivel]);

    const handleUpdateDates = (e: any) => {
        const d: (Date | null)[] = e.value;
        setDates(d.filter(d => d !== null) as Date[]);
    };

    const downloadBlobResponse = (blob: Blob, filename: string, tempo: number) => {
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        toastRef.current?.show({ severity: 'success', summary: 'Sucesso', detail: `Download do '${filename}' iniciado. Tempo: ${tempo}s.`, life: 5000 });
    };

    const processar = async () => {
        if (!dates[0] || !dates[1] || !car) return;
        setDialogProcessarVisivel(false);
        setErroProcessar(false);
        setDialogVisivel(true);

        segundosRef.current = 0;
        setSegundos(0);
        setProgresso(0);
        setApiConcluida(false);
        setMensagemLoading('Iniciando processamento do relatório...');
        inicioTimerRef.current = Date.now();

        if (intervaloProgressoRef.current) clearInterval(intervaloProgressoRef.current);

        intervaloProgressoRef.current = setInterval(() => {
            const tempoDecorrido = Date.now() - inicioTimerRef.current;
            const progressoAtual = Math.round(Math.min(100, (tempoDecorrido / CINCO_MINUTOS_MS) * 100));
            setProgresso(progressoAtual);
            if (progressoAtual >= 100) {
                clearInterval(intervaloProgressoRef.current);
                intervaloProgressoRef.current = null;
                if (!apiConcluida) setMensagemLoading('O processamento está quase lá. Apenas mais alguns instantes...');
            }
        }, 200);

        try {
            const feature = (car as any).feature;
            const start = dates[0].toISOString().split('T')[0];
            const end = dates[1].toISOString().split('T')[0];

            const url = new URL(`${API_URL}process-geojson/`);
            url.searchParams.append('start', start);
            url.searchParams.append('end', end);

            const response = await fetch(url.toString(), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(feature)
            });

            setApiConcluida(true);
            if (intervaloProgressoRef.current) clearInterval(intervaloProgressoRef.current);
            if (segundosIntervalRef.current) clearInterval(segundosIntervalRef.current);

            if (!response.ok) {
                throw new Error(await response.text() || 'Erro na requisição');
            }

            const contentType = response.headers.get('Content-Type');
            if (contentType && contentType.includes('application/pdf')) {
                const blob = await response.blob();
                setProgresso(100);
                setMensagemLoading('Relatório processado com sucesso! Preparando para download...');

                let filename = `relatorio_car_${processType}.pdf`;
                const contentDisposition = response.headers.get('Content-Disposition');
                if (contentDisposition) {
                    const match = contentDisposition.match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']+)['"]?/i);
                    if (match && match[1]) filename = decodeURIComponent(match[1]);
                }
                downloadBlobResponse(blob, filename, segundosRef.current);
                setTimeout(() => setDialogVisivel(false), 1500);
            } else {
                const errData = await response.json();
                throw new Error(errData?.detail || 'Resposta não é um PDF');
            }
        } catch (error: any) {
            setApiConcluida(true);
            if (intervaloProgressoRef.current) clearInterval(intervaloProgressoRef.current);
            setErroProcessar(true);
            setMensagemLoading('Falha ao processar o relatório: ' + (error.message || 'Erro desconhecido'));
            setTimeout(() => setDialogVisivel(false), 3000);
            toastRef.current?.show({ severity: 'error', summary: 'Erro', detail: error.message, life: 6000 });
        }
    };

    const onHideDialog = () => {
        setDialogVisivel(false);
        if (apiConcluida) {
            setProgresso(0);
            setMensagemLoading('');
        }
    };

    return (
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', height: '100vh', gap: '10px', backgroundColor: '#43C2F3' }}>
            <Toast ref={toastRef} position='center' />
            <Dialog
                onHide={() => setDialogProcessarVisivel(false)}
                visible={dialogProcessarVisivel}
                header={
                    <div style={{ display: 'flex', alignItems: 'center', height: 10 }}>
                        {processType === 'soja' ? <SojaIcon width={32} style={{ marginRight: 10 }} /> : <AlgodaoIcon width={32} style={{ marginRight: 10 }} />}
                        PROCESSAR {processType === 'soja' ? 'SOJA' : 'ALGODÃO'} (CAR)
                    </div>
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                    <label style={{ marginLeft: 15 }}>Período</label>
                    <Calendar style={{ marginLeft: 10, width: 850 }}
                        dateFormat='dd/mm/yy' onChange={handleUpdateDates} value={dates}
                        minDate={minDate} maxDate={maxDate}
                        selectionMode="range" readOnlyInput hideOnRangeSelection numberOfMonths={2} inline />
                </div>
                <Fieldset legend='Método' style={{ marginTop: '1rem' }}>
                    <span>EM DESENVOLVIMENTO</span>
                    <RadioButton disabled inputId="rl" name="processMethod" value={ProcessMethod.RegressaoLinear} onChange={(e) => setProcessMethod(e.value)} checked={processMethod === ProcessMethod.RegressaoLinear} />
                    <label htmlFor="rl" style={{ marginLeft: '0.5rem', marginRight: '2rem' }}>Regressão Linear</label>
                    <RadioButton disabled inputId="rn" name="processMethod" value={ProcessMethod.RedeNeural} onChange={(e) => setProcessMethod(e.value)} checked={processMethod === ProcessMethod.RedeNeural} />
                    <label htmlFor="rn" style={{ marginLeft: '0.5rem', marginRight: '2rem' }}>Rede Neural</label>
                </Fieldset>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
                    <Button onClick={() => processar()}>INICIAR</Button>
                </div>
            </Dialog>

            <Dialog
                header="Processando Relatório"
                visible={dialogVisivel}
                modal
                style={{ width: '50vw', minWidth: '350px' }}
                onHide={onHideDialog}
                closable={apiConcluida}
                footer={apiConcluida ? <Button label="Fechar" icon="pi pi-check" onClick={onHideDialog} autoFocus /> : null}
            >
                <div className="p-fluid" style={{ padding: '1rem 0' }}>
                    <p style={{ marginBottom: '1rem', textAlign: 'center' }}>{mensagemLoading}</p>
                    {!erroProcessar &&
                        <>
                            {!apiConcluida && <p style={{ textAlign: 'center', marginBottom: '0.5rem' }}>Tempo decorrido: {segundos} segundos</p>}
                            <ProgressBar mode="indeterminate" style={{ height: '20px' }} />
                        </>
                    }
                    {erroProcessar && <div style={{ textAlign: 'center' }}><FaRegTimesCircle size={50} color='#D22' /></div>}
                    {!apiConcluida && progresso >= 99 && progresso < 100 && (
                        <p style={{ marginTop: '1rem', textAlign: 'center', fontSize: '0.9em', color: '#6c757d' }}>Quase lá! Finalizando os últimos detalhes...</p>
                    )}
                </div>
            </Dialog>

            <Card title={<div style={{ display: 'flex', alignItems: 'center', alignContent: 'center' }}>
                    <MosaicoIcon color='#43C2F3' style={{ width: 400, marginRight: 20 }} />PROCESSAMENTO DE IMÓVEIS CAR</div>} >


                <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div className="p-field" style={{ flex: 1, minWidth: '200px' }}>
                        <label htmlFor="uf" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Estado (UF)</label>
                        <Dropdown id="uf" value={uf} options={ufs} onChange={(e) => setUf(e.value)} optionLabel="nome" placeholder="Selecione um Estado" filter style={{ width: '100%' }} />
                    </div>
                    <div className="p-field" style={{ flex: 1, minWidth: '200px' }}>
                        <label htmlFor="municipio" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Município</label>
                        <Dropdown id="municipio" value={municipio} options={municipios} onChange={onMunicipioChange} optionLabel="nome" placeholder="Selecione um Município" filter disabled={!uf || municipios.length === 0} style={{ width: '100%' }} />
                    </div>
                    <div className="p-field" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '2px', marginTop: '24px', gap: '10px' }}>
                        <Button label="Processar Soja" icon="pi pi-cog" onClick={() => { setProcessType('soja'); setDialogProcessarVisivel(true); }} disabled={!car} />
                        <Button label="Processar Algodão" icon="pi pi-cog" severity="warning" onClick={() => { setProcessType('algodao'); setDialogProcessarVisivel(true); }} disabled={!car} />
                    </div>
                </div>
            </Card>
            {loading && <ProgressBar mode="indeterminate" style={{ height: '6px' }} />}
            <div ref={mapRef} style={{ flex: 1, width: '100%', backgroundColor: '#e5e5e5', border: '1px solid #ccc', borderRadius: '4px', minHeight: '500px' }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 0, marginBottom: 0, gap: 20 }}>
                <GotaIcon color='white' width={50} height={60}/><div style={{ height: 50, border: '1px solid white' }} /><DamineIcon color='white' width={200} />
            </div>
        </div >
    );
}